// src/pages/CustomerService.js
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { trackShipment } from "../services/api";

const statusStyle = s => ({
  delivered:        { color: "#16A34A", bg: "#DCFCE7" },
  in_transit:       { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  out_for_delivery: { color: "#00A896", bg: "rgba(0,198,215,.1)" },
  pending:          { color: "#D97706", bg: "#FEF3C7" },
  picked_up:        { color: "#D97706", bg: "#FEF3C7" },
  cancelled:        { color: "#DC2626", bg: "#FEE2E2" },
}[s] || { color: "#6B7280", bg: "#EEF4F7" });

const STATUS_LABELS = {
  pending:          "Order Placed",
  picked_up:        "Picked Up",
  at_hub:           "At Sorting Hub",
  in_transit:       "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered:        "Delivered",
  cancelled:        "Cancelled",
};

export default function CustomerService() {
  const { user } = useAuth();
  const [awb, setAwb]         = useState("");
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const doTrack = async () => {
    if (!awb.trim()) return;
    setLoading(true); setError(""); setResult(null);
    const data = await trackShipment(awb.trim().toUpperCase());
    setLoading(false);
    if (data?.success) setResult(data);
    else setError("Shipment not found.");
  };

  const ship = result?.shipment;
  const timeline = result?.timeline || [];

  return (
    <div style={{ background: "#F7FAFB", minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg,#0B1E3D,#1A3460)", padding: "36px 24px 48px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <span className="tag tag-dark" style={{ marginBottom: 10, display: "inline-block" }}>CUSTOMER SERVICE</span>
            <h1 style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 28, color: "#fff" }}>
              CS Portal
            </h1>
            <p style={{ color: "rgba(255,255,255,.5)", marginTop: 4 }}>Welcome, {user?.name}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.08)", padding: "8px 14px", borderRadius: 24, border: "1px solid rgba(255,255,255,.12)" }}>
            <span className="live-dot" />
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: "rgba(255,255,255,.8)" }}>ONLINE</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>

        {/* Quick stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginTop: -24, marginBottom: 28 }}>
          {[
            { label: "Track Shipment",   icon: "🔍", desc: "Look up any order by AWB" },
            { label: "Client Support",   icon: "🎧", desc: "Handle customer queries" },
            { label: "WhatsApp",         icon: "💬", desc: "Chat with clients directly" },
          ].map(c => (
            <div key={c.label} className="card" style={{ textAlign: "center", padding: "20px 16px", boxShadow: "0 4px 16px rgba(0,0,0,.08)" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{c.icon}</div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: "#0B1E3D", marginBottom: 4 }}>{c.label}</div>
              <div style={{ color: "#9CA3AF", fontSize: 12 }}>{c.desc}</div>
            </div>
          ))}
        </div>

        {/* Track shipment */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 16 }}>
            🔍 Track a Shipment
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <input
              value={awb}
              onChange={e => setAwb(e.target.value)}
              placeholder="Enter AWB number e.g. PL-2025-00001"
              onKeyDown={e => e.key === "Enter" && doTrack()}
              style={{ flex: 1, padding: "11px 14px", border: "1.5px solid #E2EBF0", borderRadius: 8, fontSize: 14, outline: "none", fontFamily: "'Inter',sans-serif", color: "#0B1E3D" }}
            />
            <button className="btn btn-primary" onClick={doTrack} disabled={loading}>
              {loading ? "Searching…" : "Track →"}
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {ship && (
            <div className="fade-in">
              <div style={{ borderTop: "1px solid #E2EBF0", paddingTop: 20 }}>

                {/* Shipment summary */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 16, marginBottom: 20 }}>
                  {[
                    ["AWB",       ship.awb],
                    ["Status",    STATUS_LABELS[ship.status] || ship.status],
                    ["Sender",    ship.sender_name],
                    ["Recipient", ship.recipient_name],
                    ["Weight",    ship.weight_kg ? ship.weight_kg + "kg" : "—"],
                    ["Rider",     ship.rider_name || "Unassigned"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 3, fontFamily: "'JetBrains Mono',monospace", textTransform: "uppercase", letterSpacing: ".06em" }}>{k}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#0B1E3D" }}>{v}</div>
                    </div>
                  ))}
                </div>

                {/* Status badge */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                  <span style={{ padding: "5px 14px", borderRadius: 20, fontWeight: 600, fontSize: 13, background: statusStyle(ship.status).bg, color: statusStyle(ship.status).color }}>
                    {STATUS_LABELS[ship.status] || ship.status}
                  </span>
                  {ship.status === "in_transit" && <span className="live-dot" />}
                  {ship.eta && (
                    <span style={{ fontSize: 13, color: "#6B7280" }}>
                      ETA: {new Date(ship.eta).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}
                    </span>
                  )}
                </div>

                {/* Route */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 12, padding: "16px 0", borderTop: "1px solid #E2EBF0", marginBottom: 20 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>FROM</div>
                    <div style={{ fontWeight: 600, color: "#0B1E3D", fontSize: 14 }}>{ship.sender_address}</div>
                  </div>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#00C6D7,#00A896)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>→</div>
                  <div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>TO</div>
                    <div style={{ fontWeight: 600, color: "#0B1E3D", fontSize: 14 }}>{ship.recipient_address}</div>
                  </div>
                </div>

                {/* Timeline */}
                {timeline.length > 0 && (
                  <div>
                    <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: "#0B1E3D", marginBottom: 16 }}>
                      Tracking Timeline
                    </div>
                    {timeline.map((ev, i) => (
                      <div key={i} style={{ display: "flex", gap: 12, paddingBottom: i < timeline.length - 1 ? 16 : 0 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <div style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, background: i === timeline.length - 1 ? "#00C6D7" : "#00A896", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", fontWeight: 700 }}>✓</div>
                          {i < timeline.length - 1 && <div style={{ width: 2, flex: 1, marginTop: 4, background: "#00A896" }} />}
                        </div>
                        <div style={{ paddingTop: 2, paddingBottom: 4 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: i === timeline.length - 1 ? "#00A896" : "#0B1E3D" }}>
                            {STATUS_LABELS[ev.status] || ev.status}
                            {i === timeline.length - 1 && <span className="tag tag-cyan" style={{ marginLeft: 8, padding: "1px 8px", fontSize: 10 }}>CURRENT</span>}
                          </div>
                          {ev.description && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{ev.description}</div>}
                          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                            {new Date(ev.created_at).toLocaleString("en-NG")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick actions */}
                <div style={{ borderTop: "1px solid #E2EBF0", marginTop: 20, paddingTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <a href={`https://wa.me/${(ship.sender_phone || "").replace(/^0/, "234")}?text=Hello%20${ship.sender_name}%2C%20this%20is%20Pulse%20Logistics%20customer%20service.%20Your%20shipment%20${ship.awb}%20status%20is%3A%20${STATUS_LABELS[ship.status] || ship.status}.`}
                    target="_blank" rel="noopener noreferrer"
                    className="btn btn-wa btn-sm">
                    💬 WhatsApp Sender
                  </a>
                  {ship.recipient_phone && (
                    <a href={`https://wa.me/${(ship.recipient_phone || "").replace(/^0/, "234")}?text=Hello%20${ship.recipient_name}%2C%20your%20Pulse%20Logistics%20delivery%20${ship.awb}%20is%20${STATUS_LABELS[ship.status] || ship.status}.`}
                      target="_blank" rel="noopener noreferrer"
                      className="btn btn-wa btn-sm">
                      💬 WhatsApp Recipient
                    </a>
                  )}
                  <a href={`tel:${ship.sender_phone}`} className="btn btn-ghost btn-sm">
                    📞 Call Sender
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick reference */}
        <div className="card" style={{ marginBottom: 32 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, color: "#0B1E3D", marginBottom: 16 }}>
            📋 Quick Reference
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16 }}>
            {[
              { title: "Status Meanings", items: ["Pending — Order received", "Picked Up — With rider", "In Transit — On the way", "Out for Delivery — Almost there", "Delivered — Completed"] },
              { title: "Common Responses", items: ["Track your order at pulse.ng/track", "AWB sent to your WhatsApp", "Rider will call before delivery", "Estimated 1–3 business days", "Contact us: +234 800 000 0000"] },
            ].map(section => (
              <div key={section.title}>
                <div style={{ fontWeight: 600, color: "#0B1E3D", fontSize: 13, marginBottom: 10 }}>{section.title}</div>
                {section.items.map(item => (
                  <div key={item} style={{ fontSize: 12, color: "#6B7280", marginBottom: 6, display: "flex", gap: 6, alignItems: "flex-start" }}>
                    <span style={{ color: "#00A896", flexShrink: 0 }}>·</span>
                    {item}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
